import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal, ModalBody, ModalSection, Input, Spinner, MultiSelect, Switch } from '@components/ui';
import { CTA, GHOST } from '@components/ui/designTokens';
import { PencilSquareIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useUpdateUser, useRoles, useAssignableCompanies, useUser } from '../hooks/useUsers';
import Swal from 'sweetalert2';
import type { User } from '../types/user.types';
import { PASSWORD_COMPLEXITY_REGEX, PASSWORD_COMPLEXITY_MESSAGE } from '@lib/utils/passwordValidation';
import { useAuthStore } from '@features/auth/store/authStore';
import { normalizeRole } from '@lib/utils/roleNavigation';

const editUserSchema = z.object({
  email: z.string().email('Email inválido').max(100, 'Máximo 100 caracteres'),
  idNumber: z.string().min(1, 'El IDNumber es requerido').max(20, 'Máximo 20 caracteres'),
  firstName: z.string().min(1, 'El nombre es requerido').max(100, 'Máximo 100 caracteres'),
  lastName: z.string().min(1, 'El apellido es requerido').max(100, 'Máximo 100 caracteres'),
  roles: z.array(z.string()).min(1, 'Debe seleccionar al menos un rol'),
  companies: z.array(z.string()).min(1, 'Debe seleccionar al menos una compañía'),
  password: z.string().optional(),
  confirmPassword: z.string().optional(),
});

type EditUserFormData = z.infer<typeof editUserSchema>;

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

export function EditUserModal({ isOpen, onClose, user }: EditUserModalProps) {
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [changePassword, setChangePassword] = useState(false);

  // Si quien está editando el usuario tiene el rol Internal Admin, también
  // puede asignar "SUPER ADMIN" — mismo patrón de gate por rol de sesión que
  // ya usa BannerAdminPage.tsx/UserManagerPage.tsx (columna ID) y CreateUserModal.tsx.
  const sessionUser = useAuthStore((s) => s.user);
  const esInternalAdmin = normalizeRole(sessionUser?.role) === 'INTERNALADMIN';

  // Roles permitidos para editar usuarios
  const ALLOWED_ROLES = [
    'VENDEDOR', 'CUSTOMER B2B', 'COMPANY ADMIN', 'ADMINISTRATOR',
    ...(esInternalAdmin ? ['SUPER ADMIN'] : []),
  ];

  const { data: rolesData, isLoading: loadingRoles } = useRoles();
  const { data: assignableCompanies, isLoading: loadingCompanies } = useAssignableCompanies();
  // La fila de la tabla (`user`, viene de /users/search) puede traer
  // Companies/BranchOffice recortados al contexto de la compañía activa de
  // sesión, en vez del listado completo del usuario — por eso se refresca
  // con una consulta puntual por Id (GET /users/:id) al abrir el modal, que
  // sí trae la ficha completa. Mientras carga, se usa `user` como fallback
  // para no mostrar el modal vacío.
  const { data: userDetail, isLoading: loadingUserDetail } = useUser(user?.id ?? '');
  const effectiveUser = userDetail || user;

  // Las compañías que el usuario YA tiene asignadas deben verse siempre en el
  // selector, aunque queden fuera de la lista "asignable" del admin (p. ej.
  // otro admin de mayor jerarquía se las asignó) — si no se agregan aquí,
  // MultiSelect las descarta en silencio porque no están en `options`.
  // Memoizado: si se recalculara en cada render con una referencia nueva, el
  // useEffect que lo usa como dependencia se dispararía en bucle.
  const companies = useMemo(() => {
    const list = assignableCompanies || [];
    const userCompanies = effectiveUser?.companies?.length
      ? effectiveUser.companies
      : (effectiveUser?.companyId ? [{ id: effectiveUser.companyId, name: effectiveUser.companyName, code: effectiveUser.companyCode }] : []);
    const missing = userCompanies.filter((uc) => !list.some((c) => c.id === uc.id));
    return missing.length > 0 ? [...list, ...missing] : list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignableCompanies, effectiveUser?.id, effectiveUser?.companies]);
  const updateMutation = useUpdateUser();

  // Filtrar solo los roles permitidos (case-insensitive) y eliminar duplicados por nombre
  const filteredRoles = rolesData?.filter(role =>
    ALLOWED_ROLES.some(allowedRole => allowedRole.toUpperCase() === role.name.toUpperCase())
  ).reduce((unique, role) => {
    // Only add if we haven't seen this role name before (case-insensitive)
    if (!unique.some(r => r.name.toUpperCase() === role.name.toUpperCase())) {
      unique.push(role);
    }
    return unique;
  }, [] as typeof rolesData) || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
  } = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
  });

  // Populate form when user changes. Se espera a que cargue la ficha
  // completa (userDetail) antes de precargar compañías/roles —
  // usar directamente `user` (la fila de la tabla) puede traer esos arrays
  // recortados al contexto de la compañía activa de sesión.
  useEffect(() => {
    if (effectiveUser && isOpen && rolesData && !loadingUserDetail) {
      const user = effectiveUser;
      setValue('email', user.email);
      setValue('idNumber', user.idNumber || '');
      setValue('firstName', user.firstName);
      setValue('lastName', user.lastName);

      // Set roles - normalize to match API format (case-insensitive)
      const userRoles = user.roles && user.roles.length > 0 ? user.roles : [user.role];

      // Find matching role IDs from the available roles (case-insensitive)
      const matchedRoleIds = userRoles
        .map(userRole => {
          // Try to find a matching role in filteredRoles by name (case-insensitive)
          const matchedRole = filteredRoles.find(
            r => r.name.toUpperCase() === userRole.toUpperCase()
          );
          return matchedRole?.id;
        })
        .filter((id): id is string => id !== undefined);

      setSelectedRoles(matchedRoleIds);
      setValue('roles', matchedRoleIds);

      // Compañías que el usuario ya tiene asignadas (siempre visibles: `companies`
      // arriba ya las mezcla en las opciones aunque estén fuera del alcance del
      // admin logueado — el backend las conserva de todos modos al guardar).
      const userCompanyIds = (
        user.companies?.length ? user.companies.map(c => c.id) : (user.companyId ? [user.companyId] : [])
      ).filter(Boolean);
      setSelectedCompanies(userCompanyIds);
      setValue('companies', userCompanyIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveUser, isOpen, rolesData, companies, loadingUserDetail]);

  const onSubmit = async (data: EditUserFormData) => {
    if (!user) return;

    if (changePassword) {
      if (!data.password || !PASSWORD_COMPLEXITY_REGEX.test(data.password)) {
        setError('password', { message: PASSWORD_COMPLEXITY_MESSAGE });
        return;
      }
      if (data.password !== data.confirmPassword) {
        setError('confirmPassword', { message: 'Las contraseñas no coinciden' });
        return;
      }
    }

    try {
      await updateMutation.mutateAsync({
        id: user.id,
        data: {
          email: data.email,
          idNumber: data.idNumber,
          firstName: data.firstName,
          lastName: data.lastName,
          roles: selectedRoles,
          // El campo de sucursales se retiró de la UI; preservamos las que el
          // usuario ya tenía para no borrarlas al editar otros datos.
          branchOffices: effectiveUser?.branchOffices || [],
          companies: selectedCompanies,
          ...(changePassword && data.password ? { password: data.password } : {}),
        },
      });

      await Swal.fire({
        title: 'Usuario actualizado',
        text: 'El usuario ha sido actualizado exitosamente.',
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });

      reset();
      setSelectedRoles([]);
      setSelectedCompanies([]);
      setChangePassword(false);
      onClose();
    } catch (error: any) {
      Swal.fire({
        title: 'Error',
        text: error?.response?.data?.message || error.message || 'No se pudo actualizar el usuario',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
    }
  };

  const handleClose = () => {
    reset();
    setSelectedRoles([]);
    setSelectedCompanies([]);
    setChangePassword(false);
    onClose();
  };

  if (!user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Editar Usuario"
      kicker="Accesos y permisos"
      description={`${user.firstName} ${user.lastName} · ${user.email}`}
      icon={<PencilSquareIcon />}
      size="lg"
    >
      <ModalBody className="h-auto max-h-[75vh] overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-7">
          {/* ===== 01 · Alcance ===== */}
          <div>
            <ModalSection index="01" title="Alcance" />
            <p className="mb-3 flex items-start gap-1.5 text-xs text-secondary-500 dark:text-secondary-400">
              <InformationCircleIcon className="mt-[1px] h-3.5 w-3.5 flex-shrink-0 text-secondary-400" />
              Si el usuario no queda con ninguna compañía asignada, no podrá iniciar sesión en el portal.
            </p>
            <MultiSelect
              label="Compañías *"
              options={companies.map((company) => ({
                id: company.id,
                // `company.name` puede venir `null` desde Aludra (ver
                // CompanyCard.tsx/MultiSelect.tsx) — cae a `fullName` (solo
                // lo trae la fuente "asignables", no las ya asignadas al
                // usuario), luego a `code`, antes de un genérico.
                label: company.name || (company as { fullName?: string | null }).fullName || company.code || 'Sin nombre',
                description: company.code,
              }))}
              selected={selectedCompanies}
              onChange={(values) => {
                setSelectedCompanies(values);
                setValue('companies', values);
              }}
              placeholder="Selecciona una o más compañías"
              error={errors.companies?.message}
              loading={loadingCompanies}
             data-testid="user-edit-modal-companies-select"/>
          </div>

          {/* ===== 02 · Información personal ===== */}
          <div>
            <ModalSection index="02" title="Información personal" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                label="Nombre"
                placeholder="Juan"
                error={errors.firstName?.message}
                maxLength={100}
                {...register('firstName')}
               data-testid="user-edit-modal-first-name-input"/>
              <Input
                label="Apellido"
                placeholder="Pérez"
                error={errors.lastName?.message}
                maxLength={100}
                {...register('lastName')}
               data-testid="user-edit-modal-last-name-input"/>
              <Input
                label="IDNumber"
                placeholder="8-888-8888"
                error={errors.idNumber?.message}
                maxLength={20}
                {...register('idNumber')}
               data-testid="user-edit-modal-id-number-input"/>
              <Input
                label="Email"
                type="email"
                placeholder="usuario@ejemplo.com"
                disabled
                className="cursor-not-allowed bg-secondary-100 dark:bg-secondary-700"
                {...register('email')}
               data-testid="user-edit-modal-email-input"/>
            </div>
          </div>

          {/* ===== 03 · Roles ===== */}
          <div>
            <ModalSection index="03" title="Roles" />
            <p className="mb-3 flex items-start gap-1.5 text-xs text-secondary-500 dark:text-secondary-400">
              <InformationCircleIcon className="mt-[1px] h-3.5 w-3.5 flex-shrink-0 text-secondary-400" />
              Si el usuario no tiene ninguno de estos roles asignados, no podrá iniciar sesión en el portal.
            </p>
            <div className="space-y-5">
              <MultiSelect
                label="Roles *"
                options={filteredRoles.map((role, index) => ({
                  id: role.id || `role-${role.name}-${index}`,
                  label: role.name,
                  description: role.description,
                }))}
                selected={selectedRoles}
                onChange={(values) => {
                  setSelectedRoles(values);
                  setValue('roles', values);
                }}
                placeholder="Selecciona los roles"
                error={errors.roles?.message}
                loading={loadingRoles}
               data-testid="user-edit-modal-roles-select"/>
            </div>
          </div>

          {/* ===== 04 · Seguridad ===== */}
          <div>
            <ModalSection index="04" title="Seguridad" />
            <div className="rounded-xl border border-[#eef0f2] bg-[#fbfbfc] px-4 py-3.5 dark:border-secondary-700 dark:bg-secondary-900">
              <Switch
                checked={changePassword}
                onChange={(checked) => {
                  setChangePassword(checked);
                  if (!checked) {
                    setValue('password', '');
                    setValue('confirmPassword', '');
                  }
                }}
                label="Cambiar contraseña"
               data-testid="user-edit-modal-change-password-switch"/>
              {changePassword && (
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input
                    label="Nueva contraseña"
                    type="password"
                    error={errors.password?.message}
                    {...register('password')}
                   data-testid="user-edit-modal-password-input"/>
                  <Input
                    label="Confirmar contraseña"
                    type="password"
                    error={errors.confirmPassword?.message}
                    {...register('confirmPassword')}
                   data-testid="user-edit-modal-confirm-password-input"/>
                </div>
              )}
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-2.5 border-t border-[#ececec] pt-5 dark:border-secondary-700">
            <button
              type="button"
              onClick={handleClose}
              disabled={updateMutation.isPending}
              data-testid="user-edit-modal-cancel-button"
              className={GHOST}
            >
              Cancelar
            </button>
            <button
              type="submit"
              data-testid="user-edit-modal-submit-button"
              disabled={updateMutation.isPending || loadingRoles || loadingCompanies || loadingUserDetail}
              className={CTA}
            >
              {updateMutation.isPending ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Actualizando...
                </>
              ) : (
                'Actualizar Usuario'
              )}
            </button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
}
